import { useEffect, useState } from 'react';

import { AnalyticsOutlined, BarChart, Launch } from '@mui/icons-material';
import { Box, Link, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import Button from '@/components/Buttons/Button';
import CustomCheckbox from '@/components/Checkbox';
import GoLogo from '@/components/GoLogo';
import { spacing } from '@/constants/ui';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import { BaseLayout } from '@/layouts/BaseLayout';
import { CenteredLayout } from '@/layouts/CenteredLayout';
import { acceptTerms } from '@/slices/tripTokenSlice';

const Welcome = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [accepted, setAccepted] = useState(false);

  const authState = useAppSelector(state => state.tripToken);
  useEffect(() => {
    if (authState.status === 'fulfilled' && authState.terms) navigate('/markets');
  }, [authState.status, authState.terms, navigate]);

  return (
    <BaseLayout>
      <CenteredLayout>
        <GoLogo size={74} />
        <Box>
          <Typography
            sx={{ textAlign: 'center', mb: spacing[2], mt: spacing[4] }}
            variant='h1'
          >
            Welcome to GX Go!
          </Typography>
          <Typography sx={{ maxWidth: 464, textAlign: 'center', mb: spacing[2] }}>
            You're about to access robust and reliable commodity prices based on the most data,
            trusted by thousands of market leaders.
          </Typography>
        </Box>

        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #1F2634',
            background: '#0A0D21'
          }}
        >
          <Box
            display='flex'
            flexDirection='column'
            sx={{ maxWidth: 580 }}
          >
            <Box
              display='flex'
              flexDirection='column'
              mb={2}
            >
              <Box
                alignItems='center'
                display='flex'
                flexDirection='row'
                mb={spacing[1]}
              >
                <BarChart sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography
                  sx={{ ml: 1, fontWeight: 600 }}
                  variant='h5'
                >
                  Market Insights
                </Typography>
              </Box>
              <Typography sx={{ ml: 3, fontSize: 14 }}>
                See thousands of spot prices and forward curves across global energy markets,
                exploring histories, charts and trends.
              </Typography>
            </Box>
            <Box
              display='flex'
              flexDirection='column'
              mb={2}
            >
              <Box
                alignItems='center'
                display='flex'
                flexDirection='row'
                mb={spacing[1]}
              >
                <AnalyticsOutlined sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography
                  sx={{ ml: 1, fontWeight: 600 }}
                  variant='h5'
                >
                  Transparent Methodologies
                </Typography>
              </Box>
              <Typography sx={{ ml: 3, fontSize: 14 }}>
                Understand the methodology behind the index, accessing flowcharts, criteria and
                approach.
              </Typography>
            </Box>
            <Box
              display='flex'
              flexDirection='column'
            >
              <Box
                alignItems='center'
                display='flex'
                flexDirection='row'
                mb={spacing[1]}
              >
                <Launch sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography
                  sx={{ ml: 1, fontWeight: 600 }}
                  variant='h5'
                >
                  Easy Export
                </Typography>
              </Box>
              <Typography sx={{ ml: 3, fontSize: 14 }}>
                Get our data where you need it, whether that's Excel, API, Python, cloud or market
                data terminals.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: spacing[2], mt: spacing[1] }}>
          <CustomCheckbox
            checked={accepted}
            onChange={() => {
              setAccepted(prev => !prev);
            }}
          />
          <Typography sx={{ ml: 1, fontSize: 14 }}>
            I confirm that I have read and accept the{' '}
            <Link
              href='/terms.pdf'
              rel='noopener noreferrer'
              target='_blank'
              underline='hover'
            >
              Terms and Conditions
            </Link>{' '}
            and{' '}
            <Link
              href='https://www.general-index.com/privacy-cookies'
              rel='noopener noreferrer'
              target='_blank'
              underline='hover'
            >
              Privacy Policy
            </Link>
            .
          </Typography>
        </Box>

        <Button
          fullWidth
          disabled={!accepted}
          size='large'
          sx={{
            width: 250
          }}
          variant='contained'
          onClick={() => {
            dispatch(acceptTerms(new Date(Date.now()).toISOString()));
            navigate(-1);
          }}
        >
          Access GX Go
        </Button>
      </CenteredLayout>
    </BaseLayout>
  );
};

export default Welcome;
